import { ElementType, PropsWithChildren } from "react";

import { cn } from "@bentolabs/kit/utils";
import { cva, VariantProps } from "class-variance-authority";
import { twMerge } from "tailwind-merge";

import { PolymorphicProps } from "shared/types";

import { Spinner } from "./spinner";

const buttonVariants = cva(
  [
    "group",
    "relative inline-flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer transition-colors outline-solid outline-hidden border border-transparent",
    "disabled:pointer-events-none disabled:cursor-default",
  ],
  {
    variants: {
      variant: {
        "white/10": [
          "text-white bg-white/10",
          "hover:bg-white/20",
          "active:bg-white/20",
          "focus:bg-white/20",
        ],
        "nobel/10": [
          "text-nobel bg-nobel/10",
          "hover:text-white hover:bg-nobel/20",
          "active:bg-nobel/20 active:text-white",
          "focus:bg-nobel/20 focus:text-white",
        ],
        "nobel/5": [
          "text-nobel bg-nobel/5",
          "hover:text-white hover:bg-nobel/10",
          "active:bg-nobel/10 active:text-white",
          "focus:bg-nobel/10 focus:text-white",
        ],
        spearmint: [
          "bg-spearmint  text-onyx",
          "hover:bg-[#DEFCE4]",
          "active:bg-[#DEFCE4] active:brightness-110",
          "focus:bg-[#DEFCE4]",
        ],
        "spearmint/10": [
          "bg-spearmint/10 text-spearmint",
          "hover:brightness-125",
          "active:brightness-125",
          "focus:brightness-125",
        ],
        ultramarineBlue: [
          "bg-ultramarine-blue text-onyx",
          "hover:bg-[#E2E9FF]",
          "active:bg-[#E2E9FF] active:brightness-110",
          "focus:bg-[#E2E9FF]",
        ],
        pinkLemonade: [
          "bg-pink-lemonade text-white",
          "hover:brightness-110",
          "active:brightness-110",
          "focus:brightness-110",
        ],
        transparent: [
          "bg-transparent text-white",
          "hover:bg-nobel/10",
          "active:bg-nobel/10",
          "focus:bg-nobel/10",
        ],
        current: [
          "bg-current",
          "hover:brightness-200",
          "active:brightness-200",
          "focus:brightness-200",
        ],
      },
      size: {
        "38": ["h-9.5 px-4 rounded-xl"],
        "34": ["text-sm px-3 py-2.5 rounded-[10px] leading-none"],
        "56": ["text-xl h-14 leading-none"],
      },
      disabled: {
        false: null,
        true: ["cursor-default pointer-events-none"],
      },
    },

    defaultVariants: {
      variant: "white/10",
      size: "38",
    },
    compoundVariants: [
      {
        variant: "spearmint",
        disabled: true,
        className: "bg-spearmint/10 text-spearmint/50",
      },
      {
        variant: "spearmint/10",
        disabled: true,
        className: "text-spearmint/50",
      },
    ],
  }
);

type ButtonVariantsProps = VariantProps<typeof buttonVariants>;

type CommonProps = {
  className?: string;
  disabled?: boolean;
  isLoading?: boolean;
} & ButtonVariantsProps;

export type ButtonProps<E extends ElementType = "button"> = PropsWithChildren<
  PolymorphicProps<E, CommonProps>
>;

export const Button = <E extends ElementType = "button">(
  props: ButtonProps<E>
) => {
  const {
    as = "button",
    isLoading,
    children,
    className,
    disabled,
    variant = "white/10",
    size = "38",
    ...restProps
  } = props;
  const Comp = as;

  const content = isLoading ? (
    <span className="opacity-0">{children}</span>
  ) : (
    children
  );

  return (
    <Comp
      type="button"
      data-variant={variant}
      {...restProps}
      disabled={disabled || isLoading}
      className={cn(
        buttonVariants({ variant, size, disabled, className }),
        disabled && "pointer-events-none"
      )}
    >
      {isLoading && (
        <span className="absolute inset-0 flex items-center justify-center">
          <Spinner className={twMerge("size-4 text-white")} />
        </span>
      )}
      {content}
    </Comp>
  );
};

Button.Icon = ({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) => {
  return (
    <span
      className={cn(
        "absolute top-1/2 right-1.5 -translate-y-1/2",
        "transition:color inline-flex size-6.5 items-center justify-center rounded-md",
        "group-data-[variant='grey']:text-nobel group-data-[variant='grey']:bg-white/10",
        "group-hover:group-data-[variant='grey']:bg-white/20",
        className
      )}
    >
      {children}
    </span>
  );
};
